import { TaskModelType } from '@teinor/erp/types/company/task'
import { ColumnTableSchema } from '../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { CustomRenderTaskCField, CustomRenderTaskCustomer, CustomRenderTaskCustomerAndProject, CustomRenderTaskCustomerProject, CustomRenderTaskIcons, CustomRenderTaskName, CustomRenderTaskNameAndDate, CustomRenderTaskRoundImage, CustomRenderTasksFinished, CustomRenderTaskType, CustomRenderWorkingCenter } from '../../specificComponents/Task/TaskScreenTable/Subcomponents'
import useTwinTranslation from './useTwinTranslation'
import { CustomRenderDateHours, CustomRenderTime } from '../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { faUser, faUserGroup } from '@fortawesome/pro-light-svg-icons'
import TwinIcon, { TwinIconToolTip } from '../../baseComponents/TwinIcon'
import { getTaskCFields } from '../reducers/getters'
import { addDictionaryExtraLine } from '../globals/dictionary'
import { useMemo } from 'react'
import useTaskHeaderSelectorsDict from './tasks/useTaskHeaderSelectorsDict'
import { sortEmployeesByUser } from '../globals/employees'
import { sortGroupsByUser } from '../globals/groups'
import { emptyExtraLineFirstSortFunction } from '../../forms/CustomSelect/functions'
import { EmployeesPayload } from '../reducers/company/employees'
import { GroupPayload } from '../reducers/groups/groups'
import { dictIcon } from '../icons'
import { TaskTypePayload } from '../reducers/company/taskTypes'
export interface TaskModelTypeExt extends TaskModelType {
    customField: string
}

export interface TaskModelColumnsExt extends TaskModelTypeExt {
    visibles?: number
    assigned?: number
    subtask?: number
    deletedF?: string
}

interface TaskColumnsProps {
    id: string
    employees: EmployeesPayload
    groups: GroupPayload
    taskTypes: TaskTypePayload
}

const useTaskColumns = ({ id, employees, groups, taskTypes}: TaskColumnsProps) => {
    const {t} = useTwinTranslation()
    const extraColumns: ColumnTableSchema<TaskModelTypeExt> = []
    const myemployees = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(employees || {}))), [employees])
    const mygroups = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(groups || {}))), [groups])
    const myTaskTypes = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(taskTypes || {}))), [taskTypes])
    const taskCFields = getTaskCFields()
    const isMain = id === 'main' || id === ''

    const {dictAssignation, dictFinished, dictSubTasks, dictVisibles, dictDeleted } = useTaskHeaderSelectorsDict()

    const baseColumns: ColumnTableSchema<TaskModelColumnsExt> = [
        {
            id: 'finished_column',
            dataKey: 'finished',
            label: '',
            simpleLabel: t('finished', 'Finalizada'),
            customRender: (parameterValue, allRowData) => <CustomRenderTasksFinished value={String(parameterValue) === 'true'} allRowData={allRowData} width={45} />,
            width: 50
        },
        {
            id: 'id',
            dataKey: 'id',
            label: t('nº', 'Nº'),
            customRender: (parameterValue) => <div className='text-green-43'>#{parameterValue}</div>,
            sortable: true,
            width: 75
        },
        {
            id: 'nameAndDate',
            dataKey: 'name',
            label: t('title', 'Título'),
            searchKey: 'Task.name',
            sortKey: 'name',
            simpleLabel: t('titleAndDate', 'Título y fecha'),
            customRender: (_parameterValue, allRowData) => <CustomRenderTaskNameAndDate allRowData={allRowData} />,
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('title', 'Título') }
            },
            minWidth: 120
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('title', 'Título'),
            sortable: true,
            searchKey: 'Task.name',
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('title', 'Título') }
            },
            minWidth: 120,
            hideColumnByDefault: true,
            customRender: (_parameterValue, allRowData) => <CustomRenderTaskName id={String(allRowData?.id) } name={allRowData?.name} />,
        },
        {
            id: 'createdAt',
            dataKey: 'createdAt',
            label: t('createdDate', 'Fecha de creación'),
            minWidth: 165,
            sortable: true,
            customRender: (parameterValue) => <CustomRenderDateHours value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'start_date',
            dataKey: 'start_date',
            label: t('assignedDate', 'Fecha asignada'),
            minWidth: 150,
            sortable: true,
            customRender: (parameterValue) => <CustomRenderDateHours value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'subtasks',
            dataKey: 'ChildrenTasks',
            label: '',
            simpleLabel: t('subtasksAndNonVisibles', 'Subtareas y tareas no visibles'),
            customRender: (_parameterValue, allRowData) => <CustomRenderTaskIcons {...{ allRowData }} />,
            width: 50,
        },
        {
            id: 'finished',
            dataKey: 'finished',
            simpleLabel: t('finished', 'Finalizada'),
            label: t('finished', 'Finalizada'),
            justForFilter: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: { label: t('finished', 'Finalizada'), items: dictFinished }
            }
        },
        {
            id: 'visibles',
            dataKey: 'visibles',
            simpleLabel: t('visibles', 'Visibles'),
            label: t('visibles', 'Visibles'),
            justForFilter: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: { label: t('visibles', 'Visibles'), items: dictVisibles }
            }
        },
        {
            id: 'assigned',
            dataKey: 'assigned',
            simpleLabel: t('assignment', 'Asignación'),
            label: t('assignment', 'Asignación'),
            justForFilter: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: { label: t('assignment', 'Asignación'), items: dictAssignation }
            }
        },
        {
            id: 'inverted_time',
            dataKey: 'inverted_time',
            simpleLabel: t('invertedTime', 'Tiempo invertido'),
            label: t('time', 'tiempo'),
            customRender: (parameterValue) => <CustomRenderTime value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'TaskTypeId',
            dataKey: 'TaskType',
            label: isMain ? t('type', 'Tipo') : t('TaskTypeinitial', 'T'),
            simpleLabel: t('TaskType', 'Tipo de tarea'),
            customRender: (parameterValue) => <CustomRenderTaskType allRowData={parameterValue as {}} id={id} />,
            minWidth: isMain ? 100 : 50,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingleSearchBar',
                extraComponentData: {
                    items: myTaskTypes,
                    label: t('TaskType', 'Tipo de tarea'),
                    sortItems: true,
                    sortFunction: emptyExtraLineFirstSortFunction
                }
            },
            searchKey: 'TaskType.id'
        },
        {
            id: 'TaskTypeIcon',
            dataKey: 'TaskType',
            label: t('TaskTypeinitial', 'T'),
            simpleLabel: t('TaskTypeIcon', 'Tipo tar. icono'),
            customRender: (parameterValue: any) => <TwinIconToolTip icon={dictIcon[parameterValue?.icon]} className='cursor-pointer' classNameIcon='fa-lg' message={parameterValue?.name || ''} id={(parameterValue?.id || '') + 'taskType'} />,
            width: 40,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingleSearchBar',
                extraComponentData: {
                    items: myTaskTypes,
                    label: t('TaskType', 'Tipo de tarea'),
                    sortItems: true,
                    sortFunction: emptyExtraLineFirstSortFunction
                }
            },
            searchKey: 'TaskType.id',
        },
        {
            id: 'subtask',
            dataKey: 'subtask',
            simpleLabel: t('subtasks', 'Subtareas'),
            label: t('subtasks', 'Subtareas'),
            justForFilter: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: { label: t('subtasks', 'Subtareas'), items: dictSubTasks}
            }
        },
        {
            id: 'deletedF',
            dataKey: 'deletedF',
            simpleLabel: t('deletedF', 'Eliminadas'),
            label: t('deletedF', 'Eliminadas'),
            justForFilter: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: { label: t('deletedF', 'Eliminadas'), items: dictDeleted }
            }
        },
    ]
    const columnWC: ColumnTableSchema<TaskModelTypeExt> = [{
        id: 'WorkingCenterId',
        dataKey: 'WorkingCenterId',
        label: isMain ? t('workingCenter', 'Centro de trabajo') : t('workingCenterShort', 'CT'),
        simpleLabel: t('workingCenter', 'Centro de trabajo'),
        customRender: (_parameterValue, allRowData) => <CustomRenderWorkingCenter  {...{ allRowData, id }} />,
        minWidth: isMain ? 175 : 50,
    }]
    const columnCustomer: ColumnTableSchema<TaskModelTypeExt> = [
        {
            id: 'customerAndProject',
            dataKey: 'CustomerShopId',
            label: isMain ? t('customerAndProject', 'Cliente y proyecto') : t('customerInitial', 'C'),
            customRender: (_parameterValue, allRowData) => <CustomRenderTaskCustomerAndProject allRowData={allRowData} id={id} />,
            minWidth: isMain ? 175 : 50,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customerName', 'Nombre cliente') }
            },
            searchKey: 'CustomerProject.Customer.name',
        },
        {
            id: 'customer',
            dataKey: 'CustomerId',
            label: isMain ? t('customer', 'Cliente') : t('customerInitial', 'C') ,
            customRender: (_parameterValue, allRowData) => <CustomRenderTaskCustomer allRowData={allRowData} />,
            minWidth: isMain ? 175 : 50,
            hideColumnByDefault: true,
            searchKey: 'CustomerProject.Customer.name',
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customerName', 'Nombre cliente') }
            },
        },
        {
            id: 'customerProject',
            dataKey: 'CustomerProjectId',
            label: isMain ? t('project', 'Proyecto') : t('projectInitial', 'P') ,
            customRender: (_parameterValue, allRowData) => <CustomRenderTaskCustomerProject allRowData={allRowData} />,
            minWidth: isMain ? 150 : 50,
            searchKey: 'CustomerProject.name',
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customerProjectName', 'Nombre del proyecto del cliente') }
            },
        }
    ]

    const baseColumnsEnd: ColumnTableSchema<TaskModelTypeExt> = [
        {
            id: 'EmployeeId',
            dataKey: 'EmployeeId',
            simpleLabel: t('employee', 'Empleado'),
            label: ()=><TwinIcon icon={faUser} className='w-16 h-16 mx-auto' />,
            customRender: (_parameterValue, allRowData) => <CustomRenderTaskRoundImage id={allRowData?.id} value={allRowData?.EmployeeId} field='EmployeeId' items={myemployees || {}} icon={faUser} img={myemployees[allRowData?.EmployeeId]?.profile_image || allRowData?.Employee?.profile_image} name={allRowData?.Employee?.fullname_short} sortFunction={sortEmployeesByUser} />,
            width: 55,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingleSearchBar',
                extraComponentData: {
                    items: myemployees,
                    label: t('employee', 'Empleado'),
                    sortItems: true,
                    sortFunction: emptyExtraLineFirstSortFunction
                }
            }
        },
        {
            id: 'GroupId',
            dataKey: 'GroupId',
            simpleLabel: t('group', 'Grupo'),
            label: () => <TwinIcon icon={faUserGroup} className='w-16 h-16 mx-auto' />,
            customRender: (_parameterValue, allRowData) => <CustomRenderTaskRoundImage id={allRowData?.id} value={allRowData?.GroupId} field='GroupId' items={mygroups || {}} icon={faUserGroup} img={mygroups[allRowData?.GroupId]?.photo || allRowData?.Group?.photo} name={allRowData?.Group?.name} sortFunction={sortGroupsByUser} />,
            width: 55,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingleSearchBar',
                extraComponentData: {
                    items: mygroups,
                    label: t('group', 'Grupo'),
                    sortItems: true,
                    sortFunction: emptyExtraLineFirstSortFunction
                }
            }
        },
    ]

    if (taskCFields) {
        for (const customFieldId in taskCFields) {
            const customField = taskCFields[customFieldId]
            extraColumns.push(
                {
                    id: 'customField_' + customField.id,
                    dataKey: 'customField_' + customFieldId as any,
                    simpleLabel: customField.name,
                    label: isMain ? customField.name : customField.name.charAt(0).toUpperCase(),
                    customRender: (_parameterValue, allRowData) => <CustomRenderTaskCField key={allRowData?.id} id={id} allRowData={allRowData} dictTaskCFieldValues={customField.dictTaskCFieldValues} customFieldId={customFieldId} />,
                    searchableComponent: {
                        component: 'SearchableComponentsCustomSelectSearchBar',
                        extraComponentData: { items: customField.dictTaskCFieldValues, label: customField.name }
                    },
                    searchKey: 'customField_' + customField.id,
                    generalSearchable: false,
                    minWidth: isMain ? 135 : 75,
                    sortable: true,
                },
            )
            extraColumns.push(
                {
                    id: 'customField_' + customField.id+'_small',
                    dataKey: 'customField_' + customFieldId as any,
                    simpleLabel: customField.name + ' (' + t('smallVersion', 'Versión reducida')+')',
                    label: customField.name.charAt(0).toUpperCase(),
                    customRender: (_parameterValue, allRowData) => <CustomRenderTaskCField key={allRowData?.id} id={id} allRowData={allRowData} dictTaskCFieldValues={customField.dictTaskCFieldValues} customFieldId={customFieldId} isSmall={true} />,
                    searchableComponent: {
                        component: 'SearchableComponentsCustomSelectSearchBar',
                        extraComponentData: { items: customField.dictTaskCFieldValues, label: customField.name }
                    },
                    sortKey: 'customField_' + customField.id,
                    searchKey: 'customField_' + customField.id,
                    generalSearchable: false,
                    width: 75,
                    sortable: true,
                    hideColumnByDefault: true
                },
            )
        }
    }
    const columns: ColumnTableSchema<TaskModelColumnsExt> = [
        ...baseColumns,
        ...columnCustomer,
        ...columnWC,
        ...extraColumns,
        ...baseColumnsEnd
    ]
    return { columns, baseColumns, baseColumnsEnd, extraColumns, columnWC, columnCustomer }
}

export default useTaskColumns